<template>
    <div v-dark class="study-activity">
        <div v-dark class="study-activity__head">
            <div v-dark class="study-activity__title">
                <Icon type="activity" />
                <h2>Study Activity</h2>
            </div>
            <FilterDropdown
                v-model="filterOptions"
                class="study-activity__filter"
                :align="breakpoint === 'black-bear' ? 'right' : 'left'"
                size="small"
                :responsive-button="true"
                filterLabel="Filter"
                filterOptionsLabel="Filter by Quiz Mode"
                :radioEntries="[]"
                :checkboxEntries="filterOptions"
                :is-dark-mode="isDarkMode"
            />
        </div>
        <div v-if="enabledFilters.length" class="study-activity__filters">
            <div v-dark class="study-activity__filters-label">
                Filters:
            </div>
            <Pill
                v-for="filter in enabledFilters"
                :key="filter.id"
                :show-close="true"
                :is-dark-mode="isDarkMode"
                @click="removeFilter(filter.id)"
            >
                {{ filter.label }}
            </Pill>
        </div>
        <div v-if="enabledFilters.length && !studyActivities.length" class="study-activity__no-activity">
            <div v-dark class="study-activity__no-activity-label">
                There isn’t any study activity that<br>matches your filter criteria.
            </div>
        </div>
        <div v-else-if="!studyActivities.length" class="study-activity__no-activity">
            <div v-dark class="study-activity__no-activity-label">
                You haven't taken any quizzes
            </div>
            <PocketButton
                :is-dark-mode="isDarkMode"
                :disabled="isLoadingQuick10"
                @click="startQuick10"
            >
                Take a Quick 10
            </PocketButton>
        </div>
        <div v-else class="study-activity__activity-list">
            <div
                v-for="activity in studyActivities.slice(currentPage * quizzesPerPage, pageLastIndex)"
                :key="activity.quizId"
                v-dark
                class="study-activity__activity"
                tabindex="0"
                :aria-label="`${activity.name} ${activity.date} ${activity.score} ${activity.isLocked ? 'locked' : ''}`"
                @click="activityClicked(activity)"
                @keydown.enter="activityClicked(activity)"
                @mousedown.prevent
            >
                <StudyActivityCard
                    class="study-activity__activity-card"
                    :activity="activity"
                    :is-loading="isLoading"
                    :is-dark-mode="isDarkMode"
                    :current-exam="currentExam"
                />
            </div>
        </div>
        <div
            v-if="studyActivities.length"
            class="study-activity__pagination"
        >
            <TablePagination
                class="study-activity__pagination-arrows"
                :perPage="8"
                unit="quizzes"
                :currentPage="currentPage"
                :total="studyActivities.length"
                :is-dark-mode="isDarkMode"
                @changePage="changePage"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { quizModule } from '@/store/quiz/module'
import { userModule } from '@/store/user/module'
import { getScoreRange, months } from '@/utils'
import UIKit, { utils } from '@pocketprep/ui-kit'
import { qotdModule } from '@/store/qotd/module'
import { subscriptionModule } from '@/store/subscription/module'
import { mockExamModule } from '@/store/mockExam/module'
import { screenModule } from '@/store/screen/module'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { assignmentModule } from '@/store/assignment/module'
import type { TFilterOption } from '@/utils'
import StudyActivityCard from './StudyActivityCard.vue'
import type { Study } from '@pocketprep/types'

const {
    Icon,
    Button: PocketButton,
    Pill,
    FilterDropdown,
    TablePagination,
} = UIKit

type TStudyActivity = {
    quizId: string
    isLocked: boolean
    icon: string
    name: string
    nameSuffix: string
    iconColor: string
    date: string
    score: string
    scoreRange: string
}

const router = useRouter()

const isLoading = ref(true)
const isLoadingQuick10 = ref(false)
const currentPage = ref(0)
const quizzesPerPage = 8
const filterOptions = ref<TFilterOption[]>([
    {
        id: 'qotd',
        label: 'Question of the Day',
        enabled: false,
    },
    {
        id: 'quick10',
        label: 'Quick 10',
        enabled: false,
    },
    {
        id: 'timed',
        label: 'Timed Quiz',
        enabled: false,
    },
    {
        id: 'missed',
        label: 'Missed Questions',
        enabled: false,
    },
    {
        id: 'weakest',
        label: 'Weakest Subject',
        enabled: false,
    },
    {
        id: 'concept',
        label: 'Concept Quiz',
        enabled: false,
    },
    {
        id: 'custom',
        label: 'Build Your Own',
        enabled: false,
    },
])

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const pageLastIndex = computed(() => {
    const lastIndex = (currentPage.value * quizzesPerPage) + quizzesPerPage

    return lastIndex > studyActivities.value.length
        ? studyActivities.value.length
        : lastIndex
})

const enabledFilters = computed(() => {
    return filterOptions.value.reduce((acc, option) => {
        if (!option.enabled) {
            return acc
        }

        acc.push(option)
        return acc
    }, [] as TFilterOption[])
})


const todaysQotD = computed(() => {
    return qotdModule.getters.getQotDQuestion()
})

const areMockExamsAvailable = computed(() => {
    return mockExamModule.getters.getCurrentMockExams().length > 0
})

const areAssignmentsEnabled = computed(() => {
    // assignments are enabled when an org has created at least one assignment
    return assignmentModule.getters.getAreAssignmentsEnabled()
})

const sortedQuizzes = computed(() => {
    return [ ...quizModule.getters.getQuizzes() ]
        .sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        })
})

const studyActivities = computed(() => {
    const enabledFilterIds = enabledFilters.value.map((f) => f.id)

    return sortedQuizzes.value.reduce<TStudyActivity[]>((acc, q) => {
        if (enabledFilters.value.length) {
            if (
                (!enabledFilterIds.includes('qotd') && q.mode === -1)
                || (!enabledFilterIds.includes('quick10') && q.mode === 0)
                || (!enabledFilterIds.includes('missed') && q.mode === 2)
                || (!enabledFilterIds.includes('weakest') && q.mode === 3)
                || (!enabledFilterIds.includes('timed') && q.mode === 4)
                || (!enabledFilterIds.includes('mockExam') && q.mode === 5)
                || ((!enabledFilterIds.includes('levelUp') && (q.mode === 6 || q.mode === 7)))
                || (!enabledFilterIds.includes('custom') && q.mode === 10)
                || (!enabledFilterIds.includes('assignment') && q.mode === 11)
                || (!enabledFilterIds.includes('concept') && q.mode === 12)) {
                return acc
            }
        }

        // if study mode isn't in library AND isn't 1 (aka legacy), remove from quizzes
        if (q.mode !== 1 && !utils.studyModes[q.mode as -1 | 0 | 2 | 3 | 4 | 10 | 11 | 12 | 5 | 6 | 7]) {
            return acc
        }

        const { icon, name, iconColor, iconColorDM } =
            utils.studyModes[q.mode === 1 ? 0 : q.mode as -1 | 0 | 2 | 3 | 4 | 10 | 11 | 12 | 5 | 6 | 7]
        const date = new Date(q.createdAt)
        const score = Math.round((q.correctCount / (q.incorrectCount + q.correctCount)) * 100)
        const isLocked = !hasActiveSubscription.value && name === 'Question of the Day'
            && todaysQotD.value?.serial !== q.answers[0]?.questionSerial
        const prebuiltQuiz = q.prebuiltQuiz as Study.Class.PrebuiltQuizJSON
        const nameSuffix = prebuiltQuiz?.name
            ? ` ${prebuiltQuiz.name}`
            : ''

        acc.push({
            quizId: q.objectId,
            icon,
            name: name,
            nameSuffix: nameSuffix,
            isLocked,
            iconColor: isDarkMode.value ? iconColorDM : iconColor,
            date: `${months[date.getMonth()]?.slice(0, 3)} ${date.getDate()}`,
            score: q.mode === -1
                ? q.correctCount === 1
                    ? 'correct'
                    : 'incorrect'
                : `${score}%`,
            scoreRange: getScoreRange(score),
        })

        return acc
    }, [])
})

const hasActiveSubscription = computed(() => {
    return !!subscriptionModule.getters.getSubscriptionForExamId()
})

const currentExam = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

onMounted(async () => {
    await Promise.all([
        userModule.actions.fetchUserData(),
        qotdModule.actions.fetchCurrentQotDQuestion(),
        mockExamModule.actions.fetchMockExams(),
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
    ])

    const levelUpIsAvailable = await isLevelUpEnabled()
    if (levelUpIsAvailable && !filterInOpts('levelUp')) {
        filterOptions.value.push({
            id: 'levelUp',
            label: 'Level Up',
            enabled: false,
        })
    }

    if (areMockExamsAvailable.value && !filterInOpts('mockExam')) {
        filterOptions.value.push({
            id: 'mockExam',
            label: 'Mock Exam',
            enabled: false,
        })
    }

    if (areAssignmentsEnabled.value && !filterInOpts('assignment')) {
        filterOptions.value.push({
            id: 'assignment',
            label: 'Assignment',
            enabled: false,
        })
    }

    isLoading.value = false
})

const removeFilter = (filterId: string) => {
    filterOptions.value = filterOptions.value.map((o) => {
        return o.id === filterId ? { ...o, enabled: false } : o
    })
}

const filterInOpts = (filterId: string) => {
    return filterOptions.value.some((opt) => {
        return opt.id === filterId
    })
}

const isLevelUpEnabled = async () => {
    if (currentExam.value?.examGuid) {
        return await quizModule.actions.isLevelUpEnabled({
            examGuid: currentExam.value.examGuid,
        })
    }

    return false
}

const activityClicked = (activity: TStudyActivity) => {
    if (activity.name !== 'Question of the Day') {
        router.push({
            name: 'quiz-result',
            params: {
                quizId: activity.quizId,
            },
        })
    } else {
        router.push({
            name: 'qotd',
            params: {
                quizId: activity.quizId,
            },
        })
    }
}

const startQuick10 = async () => {
    if (!isLoadingQuick10.value) {
        isLoadingQuick10.value = true
        await quizModule.actions.createActiveQuiz({ mode: 'quick10', questionCount: 10 })

        router.push({ name: 'quiz' })
        isLoadingQuick10.value = false
    }
}

const changePage = (val: number) => {
    currentPage.value = val
}

const updateFilterOptions = async () => {
    const levelUpIsAvailable = await isLevelUpEnabled()
    if (areMockExamsAvailable.value && !filterInOpts('mockExam')) {
        filterOptions.value.push({
            id: 'mockExam',
            label: 'Mock Exam',
            enabled: false,
        })
    }
    if (!areMockExamsAvailable.value) {
        filterOptions.value = filterOptions.value.filter((opt) => {
            return opt.id !== 'mockExam'
        })
    }

    if (levelUpIsAvailable && !filterInOpts('levelUp')) {
        filterOptions.value.push({
            id: 'levelUp',
            label: 'Level Up',
            enabled: false,
        })
    }
    if (!levelUpIsAvailable) {
        filterOptions.value = filterOptions.value.filter((opt) => {
            return opt.id !== 'levelUp'
        })
    }

    if (areAssignmentsEnabled.value && !filterInOpts('assignment')) {
        filterOptions.value.push({
            id: 'assignment',
            label: 'Assignment',
            enabled: false,
        })
    }
    if (!areAssignmentsEnabled.value) {
        filterOptions.value = filterOptions.value.filter((opt) => {
            return opt.id !== 'assignment'
        })
    }
}

// there is not a way to look to see if an exam has changed
// but we can see if there is a change in sorted quizzes
watch(sortedQuizzes, async () => {
    await updateFilterOptions()
})

watch(filterOptions, () => {
    currentPage.value = 0
}, { deep: true })
</script>

<style lang="scss" scoped>
.study-activity {
    background: $white;
    border-radius: 6px;
    padding: 3px 16px 1px;
    box-sizing: border-box;
    min-height: 428px;
    display: flex;
    position: relative;
    flex-direction: column;

    @include breakpoint(black-bear) {
        padding: 4px 10px 0;
        min-height: 427px;
    }

    &--dark {
        color: $fog;
        background: rgba($white, 0.02);

        @include breakpoint(black-bear) {
            background: $brand-black;
        }
    }

    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 38px;
        border-bottom: 1px solid $gray-divider;
        padding-left: 8px;

        &--dark {
            border-color: $pewter;
        }

        h2 {
            margin: 0;
            font-weight: 600;
            font-size: 16px;
            line-height: 22px;
        }
    }

    &__title {
        font-size: 16px;
        line-height: 22px;
        font-weight: 600;
        display: flex;
        align-items: center;

        svg {
            height: 26px;
            width: 21px;
            margin-right: 8px;
            color: $slate;
        }

        &--dark svg {
            color: $pewter;
        }
    }

    &__filter {
        margin-right: 11px;
    }

    &__no-activity {
        margin-top: 55px;
        text-align: center;
    }

    &__no-activity-label {
        font-size: 14px;
        line-height: 19px;
        color: $slate-03;
        margin-bottom: 24px;

        &--dark {
            color: $white;
        }
    }

    &__activity-list {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    &__pagination {
        display: flex;
        justify-content: flex-end;
        height: 48px;
        align-items: center;
        outline: none;
    }

    &__pagination-arrows {
        display: flex;
        margin: 0 4px;

        :deep(.uikit-table-pagination__previous) {
            margin-right: 6px;
        }
    }

    &__filters {
        margin: 10px 0 6px;
    }

    &__filters-label {
        font-size: 13px;
        line-height: 22px;
        color: $slate-03;
        margin-right: 9px;
        display: inline-block;
        margin-left: 12px;
        vertical-align: top;

        @include breakpoint(black-bear) {
            margin-left: 0;
        }

        &--dark {
            color: $white;
        }
    }
}
</style>
